import { useEffect, useState } from "react";
import { useCurrentAnswer } from "@coffee/contexts";
import { useGetAnswerMutation } from "@coffee/data-access/api-service";
import {
  ChatContainer,
  MessageAuthor,
  useChatMessageState,
} from "@coffee/ui/components/chat";
import { LocalStorageKeys, useLocalStorage } from "@coffee/ui/hooks";
import styled from "@emotion/styled";
import { ChevronDoubleRightIcon } from "@heroicons/react/24/solid";
import { AnimatePresence, motion } from "framer-motion";
import { Icon } from "@unlikelyai-magic/ui/icons";
import {
  AnswerWidget,
  ExplanationSidebar,
  ExplanationView,
} from "./components";

const EXPLANATION_PANEL_BUTTON_WIDTH = "2.5rem";

const ChatPageContainer = styled.div<{ isExplanationOpen?: boolean }>`
  // The height of the header bars are 4rem + 3rem
  height: calc(100vh - 7rem);
  padding-top: 1rem;
  padding-bottom: 1rem;
  padding-left: 1rem;
  padding-right: ${({ isExplanationOpen }) =>
    isExplanationOpen ? "1rem" : "0"};
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacings.xl};
  width: 100vw;
  box-sizing: border-box;
`;

const ChatBoxContainer = styled(motion.div)<{ isExplanationOpen?: boolean }>`
  display: flex;
  justify-content: center;
  flex: 1 1 0%;
  min-width: 0;
  padding-left: ${({ isExplanationOpen }) => (isExplanationOpen ? "0" : "20%")};
  padding-right: ${({ isExplanationOpen }) =>
    isExplanationOpen ? "0" : "20%"};
  transition:
    padding-left 0.4s,
    padding-right 0.4s;
`;

const ChatBox = styled(ChatContainer)`
  padding: 3rem 2rem;
  width: 100%;
`;

const ExplanationPanelContainer = styled(motion.div)`
  display: flex;
  min-width: ${EXPLANATION_PANEL_BUTTON_WIDTH};
  height: 100%;
`;

const ExplanationExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: ${EXPLANATION_PANEL_BUTTON_WIDTH};
  height: 100%;
  background: ${({ theme }) => theme.colors.background[1]};
  border: none;
  border-radius: ${({ theme }) => theme.roundness.lg} 0 0
    ${({ theme }) => theme.roundness.lg};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.action.secondary.hover};
  }
  &:active {
    background: ${({ theme }) => theme.colors.action.secondary.clicked};
  }
`;

const ExplanationExpandButtonIconWrapper = styled.span<{
  isExplanationOpen?: boolean;
}>`
  display: inline-block;
  transform: ${({ isExplanationOpen }) =>
    isExplanationOpen ? "rotate(180deg)" : "rotate(0deg)"};
  transition: transform 0.2s;
  font-size: 1.5rem;
  width: 100%;
  text-align: center;
`;

const DEBOUNCED_LOADING_TIMEOUT_MS = 250;

export const Chat = () => {
  // Track the last shown answer ID to avoid duplicate restores
  const [lastRestoredAnswerId, setLastRestoredAnswerId] = useState<
    string | null
  >(null);

  const [answerIds, setAnswerIds] = useLocalStorage<string[]>(
    LocalStorageKeys.ANSWER_IDS,
    []
  );

  const { currentAnswer, setCurrentAnswer } = useCurrentAnswer();

  const slideOutAnimationDuration = 0.35;

  const { pushMessage, updateMessage, messages, reset } = useChatMessageState();

  const [getAnswerMutation, { isLoading }] = useGetAnswerMutation();

  const [debouncedIsLoading, setDebouncedIsLoading] = useState(false);
  const [isExplanationOpen, setIsExplanationOpen] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (isLoading) {
      timeout = setTimeout(() => {
        setDebouncedIsLoading(true);
      }, DEBOUNCED_LOADING_TIMEOUT_MS);
    } else {
      setDebouncedIsLoading(false);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isLoading]);

  const toggleDetailedExplanation = () => {
    setIsExplanationOpen((open) => !open);
  };

  // Restore messages when navigating from Recents (when currentAnswer changes)
  useEffect(() => {
    // Only restore if:
    // - currentAnswer is set
    // - it's a new answer (not already restored)
    // - the chat is empty (i.e., user just navigated from Recents)
    if (!currentAnswer || !currentAnswer.id) return;
    if (lastRestoredAnswerId === currentAnswer.id) return;
    if (messages.length > 0) return;

    reset(); // Clear existing messages (should be empty, but for safety)

    pushMessage({
      text: currentAnswer.question,
      author: MessageAuthor.User,
    });
    pushMessage({
      text: currentAnswer.short_answer || "No answer found.",
      author: MessageAuthor.Bot,
      widget: (
        <AnswerWidget
          citations={currentAnswer.citations}
          toggleDetailedExplanation={toggleDetailedExplanation}
        />
      ),
    });
    setLastRestoredAnswerId(currentAnswer.id);
  }, [currentAnswer, lastRestoredAnswerId, pushMessage, messages, reset]);

  const handleSendUserMessage = async (text: string) => {
    const normalisedText = text.trim();
    if (!normalisedText) return;
    setIsExplanationOpen(false); // Ensure explanation panel is closed when sending a new message
    pushMessage({
      author: MessageAuthor.User,
      text: normalisedText,
    });

    // Push the bot message first, then trigger the mutation after
    const botMessageId = pushMessage({
      author: MessageAuthor.Bot,
      text: "",
    });

    getAnswerMutation({
      question: normalisedText,
    })
      .then((result) => {
        if ("error" in result && result.error) {
          console.error("Failed to get answer from backend", result.error);
          updateMessage({
            id: botMessageId,
            text: "Sorry, I couldn't process your request. Please try again later.",
          });
          return;
        }

        if ("data" in result && result.data) {
          const short_answer = result.data.short_answer;
          const text = short_answer || "No answer found.";

          setCurrentAnswer(result.data);

          updateMessage({
            id: botMessageId,
            text,
            widget: (
              <AnswerWidget
                citations={result.data.citations}
                toggleDetailedExplanation={toggleDetailedExplanation}
              />
            ),
          });

          // Store the answer ID in local storage
          const answerId = result.data.id;
          if (answerId && !answerIds.includes(answerId)) {
            setAnswerIds([...answerIds, answerId]);
          }
        }
      })
      .catch((error) => {
        console.error("Failed to send message to backend", error);
        updateMessage({
          id: botMessageId,
          text: "Sorry, I couldn't process your request. Please try again later.",
        });
      });
  };

  const ExpandButton = () => (
    <ExplanationExpandButton
      onClick={() => setIsExplanationOpen((open) => !open)}
    >
      <ExplanationExpandButtonIconWrapper isExplanationOpen={isExplanationOpen}>
        <Icon icon={ChevronDoubleRightIcon} />
      </ExplanationExpandButtonIconWrapper>
    </ExplanationExpandButton>
  );

  return (
    <ChatPageContainer isExplanationOpen={isExplanationOpen}>
      <AnimatePresence>
        <ChatBoxContainer
          isExplanationOpen={isExplanationOpen}
          initial={{ flex: 1 }}
          animate={{ flex: 1 }}
          transition={{ duration: slideOutAnimationDuration }}
        >
          <ChatBox onSubmitUserMessage={handleSendUserMessage} />
        </ChatBoxContainer>
        {messages.length > 0 && (
          <ExplanationPanelContainer
            initial={{
              flex: 0,
            }}
            animate={{
              flex: isExplanationOpen ? 2 : 0,
            }}
            exit={{
              flex: 0,
            }}
            transition={{
              flex: { duration: slideOutAnimationDuration },
            }}
          >
            <ExpandButton />

            <ExplanationView
              isLoading={debouncedIsLoading}
              answer={currentAnswer}
            />
          </ExplanationPanelContainer>
        )}
      </AnimatePresence>
      <ExplanationSidebar
        allEvaluatedClauses={currentAnswer?.all_evaluated_clauses ?? []}
      />
    </ChatPageContainer>
  );
};
